import { AsyncAction } from 'src/utils/reduxUtils'
import { ApiService, callClientSecureApi } from './api/callApi'
import { call, put, takeLatest } from 'redux-saga/effects'
import { Endpoints } from 'src/constants/endpoints'
import { UploadUSDOTNumber, ValidateUSDOTNumber, FetchDriver } from 'src/constants/actionTypes'
import { captureSentryError } from 'src/utils/sentry'

interface ValidateUSDOTNumberRequest {
  usDotNumber: string
  stepId: string
}

interface UploadUSDOTNumberRequest {
  usDotNumber: string
  stepId: string
  lastSubmittedAt: string
  file: File
}

interface ValidateUSDOTNumberResponsePayload {
  isValid: boolean
}

function* onValidateUSDOTNumber(action: AsyncAction<ValidateUSDOTNumberRequest>) {
  const { usDotNumber, stepId } = action.payload

  try {
    const { payload }: { payload: ValidateUSDOTNumberResponsePayload } = yield call(
      callClientSecureApi,
      Endpoints.ValidateUSDOTNumber,
      {
        method: 'POST',
        body: {
          usDotNumber,
          stepId,
        },
        apiService: ApiService.Paraworks,
      },
    )

    yield put(ValidateUSDOTNumber.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.usDOTNumber.onValidateUSDOTNumber] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to validate USDOT number'

    yield put(ValidateUSDOTNumber.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onUploadUSDOTNumber(action: AsyncAction<UploadUSDOTNumberRequest>) {
  const { usDotNumber, stepId, lastSubmittedAt, file } = action.payload

  try {
    const formData = new FormData()

    formData.append('file', file)
    formData.append('stepId', stepId)
    formData.append('usDotNumber', usDotNumber)
    formData.append('lastSubmittedAt', lastSubmittedAt)

    yield call(callClientSecureApi, Endpoints.UploadDOTNumber, {
      method: 'POST',
      body: formData,
      apiService: ApiService.Paraworks,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(UploadUSDOTNumber.success())

    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.usDOTNumber.onUploadUSDOTNumber] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to upload USDOT number'

    yield put(UploadUSDOTNumber.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* usDOTNumberSaga() {
  yield takeLatest(ValidateUSDOTNumber.type.REQUEST, onValidateUSDOTNumber)
  yield takeLatest(UploadUSDOTNumber.type.REQUEST, onUploadUSDOTNumber)
}
